import { Close, CloudUpload } from '@mui/icons-material';
import { IconButton, Paper } from '@mui/material';
import notFoundImg from 'assets/images/not-found.png';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import MDBox from './MDBox';
import MDTypography from './MDTypography';

const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

const ImageUploadField = ({ value = [], onChange, name, label = 'Upload Images', accept = 'image/jpeg,image/png,image/jpg', multiple = false, disabled }) => {
    const [previews, setPreviews] = useState([]);
    const [errorMap, setErrorMap] = useState({});
    const fileInputRef = useRef(null);

    // Update previews when value prop changes
    useEffect(() => {
        if (multiple) {
            if (Array.isArray(value) && value.length > 0) {
                const filePromises = value.map((item, index) => {
                    if (typeof item === 'string') {
                        return Promise.resolve({ id: index, url: item, type: 'url' });
                    } else if (item instanceof File) {
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                resolve({ id: index, url: e.target.result, type: 'file' });
                            };
                            reader.readAsDataURL(item);
                        });
                    } else {
                        return Promise.resolve(null);
                    }
                });
                Promise.all(filePromises).then((newPreviews) => {
                    setPreviews(newPreviews.filter(Boolean));
                });
            } else {
                setPreviews([]);
            }
        } else {
            if (value != null) {
                if (typeof value === 'string') {
                    setPreviews([{ id: 0, url: value, type: 'url' }]);
                } else if (value instanceof File) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        setPreviews([{ id: 0, url: e.target.result, type: 'file' }]);
                    };
                    reader.readAsDataURL(value);
                }
            } else {
                setPreviews([]);
            }
        }
    }, [value, multiple]);

    const handleFileChange = (event) => {
        let files = Array.from(event.target.files || []);
        const allowed = accept.split(',').map(t => t.trim());
        const invalid = files.filter(f => !allowed.includes(f.type) || f.size > MAX_IMAGE_BYTES);
        if (invalid.length > 0) {
            toast.error(`Only ${allowed.map(t => t.replace('image/', '').toUpperCase()).join('/')} images up to 25MB are allowed.`, { toastId: 'imageInvalid' });
            files = files.filter(f => !invalid.includes(f));
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
        if (files.length > 0) {
            if (multiple) {
                const currentFiles = Array.isArray(value) ? value : [];
                const base64Promises = files.map((file) => {
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve(e.target.result);
                        reader.readAsDataURL(file);
                    });
                });
                Promise.all(base64Promises).then((base64Files) => {
                    onChange([...currentFiles, ...base64Files]);
                });
            } else {
                const reader = new FileReader();
                reader.onload = (e) => {
                    onChange(e.target.result);
                };
                reader.readAsDataURL(files[0]);
            }
        }
    };

    const handleRemove = (indexToRemove) => {
        if (multiple) {
            const updatedFiles = value.filter((_, index) => index !== indexToRemove);
            onChange(updatedFiles);
        } else {
            onChange(null);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <MDBox sx={{ mt: -1 }}>
            <MDTypography variant='caption' sx={{ mb: 1 }}>
                {label}
            </MDTypography>
            <br />

            <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {previews.map((preview, index) => (
                    (preview.url ? (
                        <MDBox key={preview.id} sx={{ position: 'relative', display: 'inline-block' }}>
                            <MDBox
                                onClick={handleClick}
                                component='img'
                                src={errorMap[preview.id] ? notFoundImg : preview.url}
                                alt={`Preview ${index + 1}`}
                                onError={() => {
                                    setErrorMap((prev) => ({ ...prev, [preview.id]: true }));
                                }}
                                sx={{
                                    width: 220,
                                    height: 220,
                                    objectFit: 'cover',
                                    border: 1,
                                    borderColor: 'grey.300',
                                    borderRadius: 2,
                                    cursor: disabled ? 'default' : 'pointer',
                                }}
                            />
                            {!disabled && multiple && (
                                <IconButton
                                    onClick={() => handleRemove(index)}
                                    size='small'
                                    sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        bgcolor: 'error.main',
                                        '&:hover': {
                                            bgcolor: 'error.dark',
                                        },
                                    }}
                                >
                                    <Close fontSize='small' color='white' />
                                </IconButton>
                            )}
                        </MDBox>
                    ) : null)
                ))}

                {((!multiple && previews.length === 0) || multiple) && (
                    <Paper
                        onClick={handleClick}
                        sx={{
                            width: 220,
                            height: 220,
                            border: 2,
                            borderStyle: 'dashed',
                            borderColor: 'grey.300',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: disabled ? 'default' : 'pointer',
                            '&:hover': {
                                borderColor: 'grey.400',
                            },
                        }}
                        elevation={0}
                    >
                        <MDBox sx={{ textAlign: 'center' }}>
                            <CloudUpload sx={{ color: 'grey.400', mb: 1 }} />
                            <MDTypography variant='body2' color='text'>
                                Click to upload
                            </MDTypography>
                        </MDBox>
                    </Paper>
                )}
            </MDBox>

            <input
                ref={fileInputRef}
                type='file'
                name={name}
                accept={accept}
                onChange={handleFileChange}
                multiple={multiple}
                disabled={disabled}
                style={{ display: 'none' }}
            />
        </MDBox>
    );
};

export default ImageUploadField;